/* rs-concept-browse.js — browse strip behaviour (collection pages and product pages).
 *
 * Rendered by snippets/rs-concept-browse-strip.liquid. Everything here is an ENHANCEMENT: previous,
 * next and up are real <a> elements in the markup, so crawlers, keyboards and screen readers never
 * depend on a gesture. What this file adds, 2026-10-13 rewrite (John: "needs to be actually working
 * on mobile and desktop and be intuitive, optimize touchpoints, maybe a loading swirly on page
 * switch, tight, best practice"):
 *
 *   1. SWIPE that works: direction is decided after 12px of movement (the old code dropped any
 *      gesture whose first pixels drifted vertically, which is most real thumbs), commits at 72px or
 *      a flick (>0.45px/ms over the last 100ms), never while the finger is in a sideways scroller or
 *      the product gallery, and gives LIVE FEEDBACK: a tab slides in from the edge with the
 *      destination's name while you drag and turns solid when the move will commit.
 *   2. EDGE ARROWS on fine-pointer (hover-capable) screens from 1024px up: fixed chevrons left and
 *      right, the destination's name on hover/focus, keyboard-reachable. Until 2026-10-16 they were
 *      gated at >=1500px (the width at which a 48px pill fits in the gutter beside the 1376px page),
 *      which meant John's own 1267px Chrome window and the 1440px standard check never showed them
 *      while the coach mark kept promising "edge arrows" - the report was "the desktop arrows for
 *      switching catalog pages are gone". 2026-10-16 again (John: "next arrow needs to be same as hp throughout with slide out"): the pill is now the HOME
 *      pager's arrow - a 56px ring with the theme's wide-arrow glyph, geometry in rs-concept-browse.css -
 *      and the name still slides out of it on hover/focus. This file also picks its colourway per
 *      frame: white (the home arrow) only while the whole ring is inside the CLP hero photo, ink on a
 *      white disc everywhere else. html.rs-hdr-overlay alone would be wrong for that - it stays on
 *      while the hero's bottom edge is still under the header, by which time the ring is over the page.
 *      Vertical position is a little below centre (ring centred on 50vh + 130px): clear of the Air
 *      Reviews tab, fixed at ~45% on the right, and below the collapsed filter sidebar, which is sticky
 *      from header + 30px down to about 406px. Arrow keys still work everywhere.
 *   3. LOADING STATE: the moment a move commits (swipe, arrow, key or strip link) a progress bar
 *      runs along the top and a pill names what is loading, so the page never looks frozen. Cleared
 *      on pageshow (back/forward cache) and after 8 s as a safety.
 *   4. SPEED: Speculation Rules prefetch for the two neighbours is emitted by the snippet; this file
 *      adds <link rel="prefetch"> on idle for browsers without it, so the next page's HTML is already
 *      in the cache when the gesture commits. Document-level, not prerender: no analytics double count.
 *   5. A one-time coach mark per session ("Swipe to browse" on touch, "edge arrows or ← → keys" on a
 *      pointer that got the arrows, "← → keys" on one that did not) so the affordance is learnt once
 *      without nagging and never names a control that is not on the screen; the strip's own cue stays.
 *   6. Direction is handed to the next page (sessionStorage) so the cross-document view transition
 *      in rs-concept-browse.css slides the right way; reduced-motion gets a plain cut.
 *
 * Final push 2026-10-18 (perf + commerce-regression): the coach mark (5) is armed by the visitor's FIRST
 * scroll, pointerdown, touch or key, not by load + idle. Even deferred to idle it was still the desktop LCP
 * element on the letter page (Lighthouse LCP 6.1 s); LCP measurement ends at the first input, so a pill
 * that only exists after one can never be an LCP candidate anywhere. It also sits above the PDP's sticky
 * add-to-cart bar (.rs-satc, inert while hidden) instead of covering its title and price, and the
 * per-session key is written when the pill actually shows, not when the page loads.
 */
(function () {
  'use strict';
  var HINT_KEY = 'rscx-hint-seen', DIR_KEY = 'rscx-dir', COACH_KEY = 'rscx-coach';
  var COMMIT_PX = 72, LOCK_PX = 12, FLICK_V = 0.45, PEEK_MAX = 150;
  var root = document.documentElement;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var coarse = window.matchMedia('(pointer: coarse)').matches;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function ss(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* private window */ } }
  function markSeen() { ssSet(HINT_KEY, '1'); root.classList.add('rscx-hint-seen'); }

  /* A gesture that starts in a slider or sideways scroller belongs to it. The product gallery is
     Flickity (overflow hidden + transforms), so it is matched by name, not by scrollWidth. */
  var CLAIMED = '.product__photos, .product-slideshow, [data-product-photos], [data-product-single-media-group],' +
    ' .flickity-enabled, .flickity-slider, .flickity-viewport, .product__thumbs, .product__thumbs--scroller,' +
    ' [data-rscx-ignore], input, textarea, select, [contenteditable="true"], dialog, [role="dialog"], .cart-drawer, .site-header__drawers';
  function claimed(node) {
    for (var el = node; el && el !== document.body && el.nodeType === 1; el = el.parentElement) {
      if (el.matches && el.matches(CLAIMED)) return true;
      if (el.scrollWidth > el.clientWidth + 4) { var ov = getComputedStyle(el).overflowX; if (ov === 'auto' || ov === 'scroll') return true; }
    }
    return false;
  }
  function el(tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; }
  function esc(s) { return String(s || '').replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  /* rareseeds 2026-10-21 (PLA-034): the document-level listeners (touch + keys, at the foot of init) are bound ONCE
     and delegate to `cur`, the most recently initialised strip, so a strip re-rendered in the theme editor
     (shopify:section:load re-runs boot) never leaves a second set of handlers behind - one swipe used to call go()
     twice - and the keyboard no longer keeps the FIRST strip's URLs forever. `navigating` makes go() idempotent
     per page: a second swipe or a held arrow key during the (up to 8 s) loading window is swallowed instead of
     re-assigning location; it clears with the loading state (pageshow / the 8 s safety). */
  var cur = null, navigating = false;

  function init(nav) {
    if (!nav || nav.dataset.rscxInit === '1') return;
    nav.dataset.rscxInit = '1';
    var prev = nav.getAttribute('data-rscx-prev') || '', next = nav.getAttribute('data-rscx-next') || '';
    var prevName = nav.getAttribute('data-rscx-prev-label') || 'Previous', nextName = nav.getAttribute('data-rscx-next-label') || 'Next';
    var hint = nav.querySelector('[data-rscx-hint]');
    if (hint && ss(HINT_KEY) === '1') hint.classList.add('is-done');

    /* ---- loading state ---- */
    var loader = null, loaderTimer = 0;
    function showLoading(label) {
      if (!loader) {
        loader = el('div', 'rscx-loading', '<div class="rscx-loading__bar"></div><div class="rscx-loading__pill" role="status" aria-live="polite"><span class="rscx-loading__spin" aria-hidden="true"></span><span class="rscx-loading__txt"></span></div>');
        document.body.appendChild(loader);
      }
      loader.querySelector('.rscx-loading__txt').textContent = label ? 'Loading ' + label : 'Loading';
      loader.classList.add('is-on');
      clearTimeout(loaderTimer); loaderTimer = setTimeout(hideLoading, 8000);
    }
    function hideLoading() { navigating = false; if (loader) loader.classList.remove('is-on'); }
    window.addEventListener('pageshow', hideLoading);
    window.addEventListener('pagehide', function () { clearTimeout(loaderTimer); });

    function go(dir) {
      var url = dir < 0 ? prev : next, label = dir < 0 ? prevName : nextName;
      if (!url) return false;
      if (navigating) return true; /* PLA-034: already leaving - swallow the repeat (true keeps the key default-prevented) */
      navigating = true;
      markSeen(); ssSet(DIR_KEY, dir < 0 ? 'prev' : 'next');
      showLoading(label);
      window.location.href = url;
      return true;
    }
    /* strip links: same loading state, same direction hand-off, default navigation */
    nav.addEventListener('click', function (e) {
      var a = e.target && e.target.closest && e.target.closest('a'); if (!a) return;
      markSeen();
      var isPrev = a.classList.contains('rscx__side--prev'), isNext = a.classList.contains('rscx__side--next');
      if (isPrev || isNext) { ssSet(DIR_KEY, isPrev ? 'prev' : 'next'); showLoading(isPrev ? prevName : nextName); }
      else showLoading(a.textContent.trim());
    });

    /* ---- prefetch fallback (Speculation Rules come from the snippet) ---- */
    function prefetch() {
      if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) return;
      [prev, next].forEach(function (u) { if (!u) return; var l = el('link'); l.rel = 'prefetch'; l.href = u; l.as = 'document'; document.head.appendChild(l); });
    }
    if ('requestIdleCallback' in window) requestIdleCallback(prefetch, { timeout: 3000 }); else setTimeout(prefetch, 1500);

    /* ---- edge arrows (fine pointer, 1024px and up; the CSS hides them again for hover:none / coarse) ---- */
    var edgesShown = false;
    /* 2026-10-19 (John, from his phone: "no forward and back arrows on mobile ... move the text on the CLP
       image slightly right to fit a back button and the other side needs a forward button"): on coarse
       pointers the two arrows are built too, but ONLY in the on-hero placement - 40px rings at the title's
       vertical centre, 12px from each edge (.rscx-edges--touch in the CSS; the hero gives its text a 64px
       left gutter and the title a right gutter under 768px). There is no fixed fallback line on a phone:
       once the hero scrolls away the arrows go with it and the swipe takes over, as before. */
    /* 2026-10-19 evening (John: the story page after home "needs arrows nav" on his phone, and its strip's black
       arrows were "a different color than anywhere else"): a story page's photo (.rs-fam__hero, sections/
       rs-story-page.liquid) counts as a hero too. It has no title band, so the rings sit at the PHOTO's vertical
       centre; on phones the story page keeps the solid header (no html.rs-has-hero), hence the separate test. */
    var famHero = document.querySelector('.rs-fam__hero');
    var touchHero = !fine && ((root.classList.contains('rs-has-hero') && !!document.querySelector('.rs-clp-hero .rs-clp-hero__title')) || !!famHero);
    if ((fine && window.matchMedia('(min-width: 1024px)').matches) || touchHero) {
      edgesShown = !!fine;
      /* A <nav> with its own name, not a bare <div>: the container sits in <body> outside <main>, so
         axe 4.10 flagged it under "region" (content outside any landmark) - and role="presentation"
         does NOT clear that, the two links are still content with no landmark above them (tested
         2026-10-16 with all three variants; only a navigation landmark passes). The strip's own
         <nav> is "Browse", so this one has a distinct name for the landmark-unique rule. */
      var edges = el('nav', 'rscx-edges');
      edges.setAttribute('aria-label', 'Browse arrows');
      if (prev) edges.appendChild(edgeLink(-1, prev, prevName));
      if (next) edges.appendChild(edgeLink(1, next, nextName));
      if (!fine) edges.classList.add('rscx-edges--touch');
      var heroEl = famHero || (root.classList.contains('rs-has-hero') ? document.querySelector('.rs-clp-hero') : null);
      /* rareseeds 2026-10-21 (PLA-031): the <nav> goes into the DOM where it is SEEN - right after the hero's section on
         a CLP / story page, else first inside <main> - so the two rings are the tab stops beside the hero instead of
         the last two on the page, after the whole grid and the footer (they were focusables 144-145 of 146; WCAG
         2.4.3). Positioning is unaffected: every ancestor from <main> up is position:static with no transform
         (checked on live 2026-10-21), so position:fixed and the document-space absolute of .rscx-edges--hero resolve
         against the viewport / initial containing block exactly as they did from <body>. The landmark note above
         still holds: it is a named <nav> in its own right wherever it sits. */
      var heroSec = heroEl ? (heroEl.closest('.shopify-section') || heroEl) : null, mainEl = document.querySelector('main');
      if (heroSec && heroSec.parentNode) heroSec.insertAdjacentElement('afterend', edges);
      else if (mainEl) mainEl.insertAdjacentElement('afterbegin', edges);
      else document.body.appendChild(edges);
      /* Two placements, one watcher (one rAF per scroll/resize burst; the hero is looked up once -
         sections/rs-clp-hero.liquid marks html.rs-has-hero inline before first paint, long before this
         deferred script runs).
         ON THE HERO (2026-10-16 John: "move the arrows onto the CLP image on desktop next to the name and
         on the other side; make them slightly smaller"): while the page has a CLP hero with a title, the
         two arrows sit on the photo at the title's vertical centre - prev at the left edge beside the
         name, next at the right edge - as 48px rings (.rscx-edges--hero in the CSS), in the light
         colourway because the title band is over the photo's scrim. The container goes position:absolute
         in DOCUMENT space (the custom property is the title's centre plus scrollY, read on load, fonts
         ready and resize), so the rings scroll WITH the hero instead of chasing it a frame late from a
         passive scroll handler. The mode ends the moment the ring would no longer fit between the sticky
         header's bottom edge (0 while html.rs-hdr-hidden has slid it away) and the hero's bottom edge;
         then the rings fall back to the fixed 50vh+130 line and the 56px ring, exactly as on a page
         without a hero. .rs-clp-hero__title is the anchor (the hero section keeps that class and, from
         1024px, a 92px gutter so the prev ring clears the name - verify by measuring, not by reading).
         FALLBACK colourway: .rscx-edges--light only while the whole 56px ring would sit inside the hero
         photo, computed from the fixed line analytically (no second layout read). */
      var titleEl = heroEl ? heroEl.querySelector('.rs-clp-hero__title') : null; /* null on a story page: the photo itself is the anchor */
      var hdrEl = document.querySelector('.header-section');
      if (heroEl) {
        var lightTick = false, onHero = false;
        var paintEdges = function () {
          lightTick = false;
          var h = heroEl.getBoundingClientRect(), want = false, cy = 0;
          if (titleEl || famHero) {
            var hdrBottom = hdrEl ? Math.max(0, hdrEl.getBoundingClientRect().bottom) : 0;
            var t = titleEl ? titleEl.getBoundingClientRect() : h;
            /* no title band (story page): sit where a CLP title would, 72% down the photo, not at its centre
               (John, 2026-10-19: "arrows are up way higher on mobile on that same page") */
            cy = titleEl ? (t.top + t.bottom) / 2 : h.top + h.height * 0.72;
            var half = fine ? 24 : 20; /* 48px ring on desktop, 40px on touch */
            want = t.height > 0 && cy - half >= hdrBottom && cy + half <= h.bottom;
          }
          if (want) edges.style.setProperty('--rscx-edge-y', Math.round(cy + window.scrollY) + 'px');
          if (want !== onHero) { onHero = want; edges.classList.toggle('rscx-edges--hero', want); }
          var fy = window.innerHeight / 2 + 130; /* the fallback line (CSS: top 50vh + 130px, 56px ring) */
          edges.classList.toggle('rscx-edges--light', want || (h.top <= fy - 28 && h.bottom >= fy + 28));
        };
        var onMove = function () { if (!lightTick) { lightTick = true; requestAnimationFrame(paintEdges); } };
        paintEdges();
        window.addEventListener('scroll', onMove, { passive: true });
        window.addEventListener('resize', onMove);
        window.addEventListener('load', onMove);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(onMove);
      }
    }
    function edgeLink(dir, url, name) {
      var a = el('a', 'rscx-edge rscx-edge--' + (dir < 0 ? 'prev' : 'next'));
      a.href = url; a.setAttribute('aria-label', (dir < 0 ? 'Previous: ' : 'Next: ') + name);
      /* The ring is the home arrow's circle. The chev still carries the entity, as before; the CSS hides it
         (font-size:0) and masks the theme's wide-arrow glyph onto currentColor in its place. */
      a.innerHTML = '<span class="rscx-edge__ring" aria-hidden="true"><span class="rscx-edge__chev">' + (dir < 0 ? '&larr;' : '&rarr;') + '</span></span><span class="rscx-edge__name">' + esc(name) + '</span>';
      /* Let the browser have the click when the visitor asked for a new tab or window:
         ctrl/cmd-click, shift-click, middle-click and any modified click are real browsing
         gestures, and swallowing them with preventDefault() navigated the CURRENT tab
         instead - the strip's own prev/next links never had this fault because they let the
         anchor default through. Only a plain left click is ours to animate. */
      a.addEventListener('click', function (e) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault(); go(dir);
      });
      return a;
    }

    /* ---- swipe with live feedback ---- */
    var peek = null;
    function getPeek() {
      if (!peek) { peek = el('div', 'rscx-peek', '<span class="rscx-peek__chev" aria-hidden="true"></span><span class="rscx-peek__name"></span>'); peek.setAttribute('aria-hidden', 'true'); document.body.appendChild(peek); }
      return peek;
    }
    function showPeek(dir, progress, armed) {
      var p = getPeek(); var name = dir < 0 ? prevName : nextName;
      p.className = 'rscx-peek is-on rscx-peek--' + (dir < 0 ? 'prev' : 'next') + (armed ? ' is-armed' : '');
      p.querySelector('.rscx-peek__chev').innerHTML = dir < 0 ? '&larr;' : '&rarr;';
      p.querySelector('.rscx-peek__name').textContent = name;
      var shift = Math.min(PEEK_MAX, progress * PEEK_MAX);
      p.style.transform = 'translate(' + (dir < 0 ? (-PEEK_MAX + shift) : (PEEK_MAX - shift)) + 'px, -50%)';
      p.style.opacity = String(Math.min(1, 0.35 + progress));
    }
    function hidePeek() { if (peek) { peek.classList.remove('is-on'); peek.style.opacity = '0'; peek.style.transform = ''; } }
    function armPeek() { if (peek) peek.classList.add('is-armed'); }
    /* PLA-034: this strip is now the one the document-level handlers below act on */
    cur = { prev: prev, next: next, go: go, showPeek: showPeek, hidePeek: hidePeek, armPeek: armPeek };

    /* ---- document-level handlers: bound once per page, never per strip (PLA-034). A later init (theme editor
         re-render) stops here: the coach mark at the foot is per session and the first strip already armed it. ---- */
    if (init.docBound) return;
    init.docBound = true;
    var x0 = null, y0 = null, t0 = 0, dir = 0, lockedH = false, abandoned = false, lastX = 0, lastT = 0, vel = 0;
    document.addEventListener('touchstart', function (e) {
      if (!cur || !e.touches || e.touches.length !== 1 || claimed(e.target)) { x0 = null; return; }
      /* 2026-10-19: a touch that starts on a product card's PHOTO when that card has image slides belongs to the card
         (assets/rs-global.js steps the images); the rest of the card and the gaps still turn the page. */
      if (window.__rsCardPhotoAt && window.__rsCardPhotoAt(e.target, e.touches[0].clientX, e.touches[0].clientY)) { x0 = null; return; }
      x0 = lastX = e.touches[0].clientX; y0 = e.touches[0].clientY; t0 = lastT = e.timeStamp; dir = 0; lockedH = false; abandoned = false; vel = 0;
    }, { passive: true });
    document.addEventListener('touchmove', function (e) {
      if (x0 === null || abandoned || !e.touches || !e.touches.length) return;
      var x = e.touches[0].clientX, y = e.touches[0].clientY, dx = x - x0, dy = y - y0;
      if (!lockedH) {
        if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return;      // undecided yet
        if (Math.abs(dy) > Math.abs(dx)) { abandoned = true; hidePeek(); return; } // vertical scroll: theirs
        lockedH = true;
      }
      var now = e.timeStamp; if (now > lastT) { vel = (x - lastX) / (now - lastT); lastX = x; lastT = now; }
      var d = dx < 0 ? 1 : -1; if (d !== dir) dir = d;
      if ((dir > 0 && !cur.next) || (dir < 0 && !cur.prev)) { cur.hidePeek(); return; }
      var progress = Math.min(1, Math.abs(dx) / COMMIT_PX);
      cur.showPeek(dir, progress, Math.abs(dx) >= COMMIT_PX || Math.abs(vel) >= FLICK_V);
    }, { passive: true });
    function endTouch(e) {
      if (x0 === null) return;
      var t = (e.changedTouches && e.changedTouches[0]); var dx = t ? t.clientX - x0 : 0;
      var ok = lockedH && !abandoned && dir !== 0 && (Math.abs(dx) >= COMMIT_PX || (Math.abs(vel) >= FLICK_V && Math.abs(dx) >= LOCK_PX * 2)) && ((dir > 0 && dx < 0) || (dir < 0 && dx > 0));
      x0 = null;
      if (ok && cur.go(dir)) { cur.armPeek(); return; }
      cur.hidePeek();
    }
    document.addEventListener('touchend', endTouch, { passive: true });
    document.addEventListener('touchcancel', function () { x0 = null; if (cur) cur.hidePeek(); }, { passive: true });

    /* ---- keyboard ---- */
    document.addEventListener('keydown', function (e) {
      if (!cur || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      var a = document.activeElement;
      if (a && a !== document.body && a !== root && !a.classList.contains('rscx-edge')) return;
      if (e.key === 'ArrowRight') { if (cur.go(1)) e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { if (cur.go(-1)) e.preventDefault(); }
    });

    /* ---- one-time coach mark per session ---- */
    if (ss(COACH_KEY) !== '1' && ss(HINT_KEY) !== '1' && (prev || next)) {
      var msg = coarse && !fine ? 'Swipe left or right to browse ' + (nav.getAttribute('data-rscx-what') || 'collections')
        : (edgesShown ? 'Use the edge arrows or ← → keys to browse ' : 'Use the ← → keys to browse ') + (nav.getAttribute('data-rscx-what') || 'collections');
      // Final audit 2026-10-18: appended at DOMContentLoaded this pill became the desktop LCP element on the
      // letter page (5.6-6.5 s); after load + idle it still was (6.1 s). Final push, same day: it is now armed
      // by the first scroll / pointerdown / touch / key - LCP measurement stops at the first input, so the pill
      // can never be the LCP on any page - and it clears the PDP's sticky add-to-cart bar.
      var showCoach = function () {
        if (ss(COACH_KEY) === '1') return;
        ssSet(COACH_KEY, '1');
        var coach = el('div', 'rscx-coach', '<span class="rscx-coach__glyph" aria-hidden="true">&larr;&nbsp;&rarr;</span><span></span>');
        coach.setAttribute('role', 'status'); coach.lastChild.textContent = msg;
        /* layout values, not the rect: the bar may still be sliding in (transform) when the pill is made */
        var satc = document.querySelector('.rs-satc:not([inert])');
        if (satc && satc.offsetHeight > 0) { var sb = parseFloat(getComputedStyle(satc).bottom) || 0; coach.style.bottom = Math.round(sb + satc.offsetHeight + 16) + 'px'; }
        document.body.appendChild(coach);
        requestAnimationFrame(function () { coach.classList.add('is-on'); });
        setTimeout(function () { coach.classList.remove('is-on'); setTimeout(function () { coach.remove(); }, 600); }, reduce ? 3000 : 4200);
      };
      var ARM_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'scroll'], armed = false;
      var arm = function () {
        if (armed) return; armed = true;
        ARM_EVENTS.forEach(function (t) { window.removeEventListener(t, arm, true); });
        if ('requestIdleCallback' in window) requestIdleCallback(showCoach, { timeout: 1500 }); else setTimeout(showCoach, 300);
      };
      ARM_EVENTS.forEach(function (t) { window.addEventListener(t, arm, { capture: true, passive: true }); });
    }
  }

  function boot() { var list = document.querySelectorAll('[data-rscx]'); for (var i = 0; i < list.length; i++) init(list[i]); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
